import React, { useState } from 'react';
import { Box, Paper, Typography, Tabs, Tab, Stack, Tooltip } from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import { InfoTooltip } from '@shared/components/InfoTooltip';
import type { PlaybookSection } from '../types';
import DropdownsTab from '../components/DropdownsTab';
import DynamicFieldsTab from '../components/DynamicFieldsTab';
import FieldMappingTab from '../components/FieldMappingTab';
import PriceMappingTab from '../components/PriceMappingTab';
import { Checklist, DisplaySettings, AttachMoney } from '@mui/icons-material';
import CampaignIcon from '@mui/icons-material/Campaign';

const sections: Array<{ id: PlaybookSection; icon: React.ReactNode; name: string }> = [
  { id: 'Offer', name: 'Offers', icon: <LocalOfferIcon fontSize="small" /> },
  { id: 'AB Testing', name: 'AB Testing', icon: <Checklist fontSize="small" /> },
  { id: 'TMC Campaigns', name: 'TMC Campaigns', icon: <CampaignIcon fontSize="small" /> },
];

const PlaybookPage: React.FC = () => {
  // Use index for tab/section for easy sync with Tabs
  const [sectionIdx, setSectionIdx] = useState(0);
  const [tab, setTab] = useState(0);
  const section = sections[sectionIdx].id;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4">Playbook</Typography>
        <InfoTooltip
          title={
            <>Configure global lookup lists and per-section dynamic fields used across Offer, TMC, and A/B Test.</>
          }
        />
      </Stack>

      {/* Section Tabs - Horizontal */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={sectionIdx}
          onChange={(_, idx) => setSectionIdx(idx)}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          {sections.map((s, i) => (
            <Tab
              key={s.id}
              disabled={i > 0}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {s.icon}
                  <span>{s.name}</span>
                </Box>
              }
              sx={{
                fontWeight: 600,
                fontSize: 16,
                textTransform: 'none',
                minHeight: 48,
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Vertical Tabs Layout */}
      <Paper sx={{ display: 'flex', minHeight: '70vh' }}>
        {/* Left Side - Vertical Tabs */}
        <Box
          sx={{
            width: 240,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
          }}
        >
          <Tabs
            orientation="vertical"
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              '& .MuiTab-root': {
                alignItems: 'flex-start',
                textAlign: 'left',
                minHeight: 56,
                px: 3,
                py: 2,
              },
            }}
          >
            <Tooltip title="Configure how frontend field names map to backend API field names" placement="right" arrow>
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <DisplaySettings fontSize="small" />
                    <span>Field Mapping</span>
                  </Box>
                }
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            </Tooltip>
            <Tooltip
              title="Manage dropdown options and lookup lists used across the application"
              placement="right"
              arrow
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <ListAltIcon fontSize="small" />
                    <span>Dropdowns</span>
                  </Box>
                }
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            </Tooltip>
            <Tooltip
              title="Define custom fields that can be added dynamically to forms and displays"
              placement="right"
              arrow
            >
              <Tab
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                    <DynamicFeedIcon fontSize="small" />
                    <span>Dynamic Fields</span>
                  </Box>
                }
                sx={{ textTransform: 'none', fontWeight: 500 }}
              />
            </Tooltip>
            {section === 'Offer' && (
              <Tooltip title="Map prices to platform-specific values for offer configurations" placement="right" arrow>
                <Tab
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <AttachMoney fontSize="small" />
                      <span>Price Mapping</span>
                    </Box>
                  }
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                />
              </Tooltip>
            )}
          </Tabs>
        </Box>

        {/* Right Side - Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {tab === 0 && <FieldMappingTab section={section} />}
          {tab === 1 && <DropdownsTab section={section} />}
          {tab === 2 && <DynamicFieldsTab section={section} />}
          {tab === 3 && section === 'Offer' && <PriceMappingTab />}
        </Box>
      </Paper>
    </Box>
  );
};

export default PlaybookPage;
